/* eslint-disable max-lines */
/* eslint-disable max-lines-per-function */
/* eslint-disable @typescript-eslint/no-misused-promises */
import React from 'react'
import { Field, Form } from 'react-final-form'
import { useTranslation } from 'react-i18next'
import { formatPrice } from 'src/helpers/formatPrice'
import { InvoiceFormData, InvoicePriceType, validateInvoiceForm } from 'src/helpers/invoiceFormValidation'
import { MarketPlaceMotorbike } from 'src/redux/motorbikesReducer'
import FormDateInput from '../atoms/FormDateInput'
import FormSelect from '../atoms/FormSelect'
import { fieldBoxShadow, FormFieldError, FormFieldLabel, FormTextArea, FormTextInput } from '../atoms/FormTextInput'
import InsetSegmentedControl from '../atoms/InsetSegmentedControl'
import CheckSvg from '../other/svg/CheckSvg'
import { Client } from 'src/redux/clientsReducer'
import ClientSearchSelect from './ClientSearchSelect'
import FormFooter from './FormFooter'
import PriceBreakdownCard from './PriceBreakdownCard'
import VehicleSearchSelect from './VehicleSearchSelect'
import { vehicleFieldsFromMotorbike } from './SellContractForm'

interface InvoiceFormProps {
  initialValues: Partial<InvoiceFormData>
  onSubmit: (values: InvoiceFormData) => void
  onCancel: () => void
  mode: 'create' | 'edit'
  loading?: boolean
}

interface FieldMeta {
  error?: string
  touched?: boolean
  submitFailed?: boolean
}

const fieldClass = 'self-stretch flex flex-col justify-start items-start gap-2'
const cardShadow =
  '0px 4px 8px 0px rgba(0,0,0,0.08), 0px 2px 4px 0px rgba(0,0,0,0.08), 0px 0px 0px 1px rgba(0,0,0,0.08)'
const numericPricePattern = /^\d*[.,]?\d*$/

type VatLabelKey =
  | 'sell_contract.vat_23'
  | 'sell_contract.vat_10'
  | 'sell_contract.vat_5'
  | 'sell_contract.vat_marginal'
  | 'sell_contract.vat_small_business'

interface VatOption {
  value: string
  labelKey: VatLabelKey
  rate: number
}

const vatOptions: VatOption[] = [
  { value: 'vat_23', labelKey: 'sell_contract.vat_23', rate: 23 },
  { value: 'vat_10', labelKey: 'sell_contract.vat_10', rate: 10 },
  { value: 'vat_5', labelKey: 'sell_contract.vat_5', rate: 5 },
  { value: 'marginal_taxation', labelKey: 'sell_contract.vat_marginal', rate: 0 },
  { value: 'small_business', labelKey: 'sell_contract.vat_small_business', rate: 0 }
]

const isFieldInvalid = (meta: FieldMeta): boolean =>
  Boolean(meta.error) && (Boolean(meta.touched) || Boolean(meta.submitFailed))

const selectedId = (value: string | number | undefined): string => {
  if (typeof value === 'number' && value !== 0) {
    return String(value)
  }
  if (typeof value === 'string' && value !== '' && value !== '0') {
    return value
  }
  return ''
}

const toMoneyNumber = (value: string | number | undefined): number => {
  const parsed = Number(value)
  if (Number.isNaN(parsed)) {
    return 0
  }
  return parsed
}

const roundMoney = (value: number): number => Math.round(value * 100) / 100

const vatRateForOption = (option: string): number => vatOptions.find((item) => item.value === option)?.rate ?? 0

const applyClientFields = (change: (name: keyof InvoiceFormData, value: string) => void, client: Client): void => {
  change('client_id', client.id)
  change('client_name', client.name)
  change('client_tax_number', client.tax_number ?? '')
  change('client_street', client.street ?? '')
  change('client_house_number', client.house_number ?? '')
  change('client_zip_code', client.zip_code ?? '')
  change('client_place', client.place ?? '')
}

const mileageDisplayValue = (mileage: number | null | undefined, kmLabel: string): string => {
  if (typeof mileage !== 'number') {
    return '-'
  }
  return `${formatPrice(mileage)} ${kmLabel}`
}

const displayOrDash = (value: string | undefined): string => {
  if (typeof value !== 'string' || value.length === 0) {
    return '-'
  }
  return value
}

const clearClientFields = (change: (name: keyof InvoiceFormData, value: string) => void): void => {
  change('client_id', '')
  change('client_name', '')
  change('client_tax_number', '')
  change('client_street', '')
  change('client_house_number', '')
  change('client_zip_code', '')
  change('client_place', '')
}

const priceBreakdown = (
  values: Partial<InvoiceFormData>
): {
  netPrice: number
  grossPrice: number
  vatAmount: number
  vatRate: number
} => {
  const price = toMoneyNumber(values.price)
  const vatRate = vatRateForOption(values.vat_option ?? '')
  const priceType: InvoicePriceType = values.price_type ?? 'gross'
  const netPrice = priceType === 'gross' ? roundMoney(price / (1 + vatRate / 100)) : price
  const grossPrice = priceType === 'net' ? roundMoney(price * (1 + vatRate / 100)) : price
  return { netPrice, grossPrice, vatAmount: roundMoney(grossPrice - netPrice), vatRate }
}

const currencySymbol = (currency: string | undefined): string => (currency === 'EUR' ? '€' : 'zł')

const InvoiceForm = ({ initialValues, onSubmit, onCancel, mode, loading = false }: InvoiceFormProps): JSX.Element => {
  const { t } = useTranslation()

  return (
    <Form
      onSubmit={(values) => {
        onSubmit({ ...values, net_price: priceBreakdown(values).netPrice })
      }}
      validate={validateInvoiceForm}
      initialValues={initialValues}
      render={({ handleSubmit, submitting, dirty, values, form }) => {
        const { netPrice, grossPrice, vatAmount, vatRate } = priceBreakdown(values)
        const symbol = currencySymbol(values.currency_code)
        const priceType = values.price_type ?? 'gross'

        return (
          <form
            onSubmit={handleSubmit}
            className="self-stretch flex-1 min-h-0 relative bg-backgrounds-bg-field-component rounded-lg inline-flex flex-col justify-start items-center overflow-hidden"
            style={{ boxShadow: cardShadow }}>
            <div className="self-stretch h-px bg-borders-border-base" />
            <div className="w-full max-w-[1000px] min-w-[720px] px-4 pt-4 pb-6 flex-1 min-h-0 flex flex-col justify-start items-start gap-6 overflow-y-auto [&>*]:shrink-0">
              <div className="text-zinc-900 text-xl font-bold font-Montserrat leading-7">
                {t('invoice_form.invoice_data')}
              </div>
              <div className="self-stretch grid grid-cols-2 gap-x-8 gap-y-4">
                <Field name="invoice_number">
                  {({ input, meta }) => (
                    <div className={fieldClass}>
                      <FormFieldLabel label={t('invoice_form.invoice_number')} />
                      <FormTextInput
                        value={typeof input.value === 'string' ? input.value : ''}
                        onChange={input.onChange}
                        placeholder={t('invoice_form.invoice_number_placeholder')}
                        error={isFieldInvalid(meta)}
                      />
                      <FormFieldError show={isFieldInvalid(meta)} message={t('sell_contract.field_required')} />
                    </div>
                  )}
                </Field>
                <Field name="language">
                  {({ input, meta }) => (
                    <div className={fieldClass}>
                      <FormFieldLabel label={t('invoice_form.invoice_language')} />
                      <FormSelect
                        value={typeof input.value === 'string' ? input.value : ''}
                        onChange={input.onChange}
                        placeholder={t('sell_contract.select')}
                        error={isFieldInvalid(meta)}
                        options={[
                          { value: 'pl', label: t('sell_contract.language_pl') },
                          { value: 'en', label: t('invoice_form.language_en') }
                        ]}
                      />
                      <FormFieldError show={isFieldInvalid(meta)} message={t('sell_contract.field_required')} />
                    </div>
                  )}
                </Field>
                <Field name="invoice_date">
                  {({ input, meta }) => (
                    <div className={fieldClass}>
                      <FormFieldLabel label={t('invoice_form.invoice_date')} />
                      <FormDateInput
                        value={typeof input.value === 'string' ? input.value : ''}
                        onChange={input.onChange}
                        onBlur={input.onBlur}
                        placeholder={t('sell_contract.date_placeholder')}
                        error={isFieldInvalid(meta)}
                      />
                      <FormFieldError show={isFieldInvalid(meta)} message={t('sell_contract.field_required')} />
                    </div>
                  )}
                </Field>
                <Field name="delivery_date">
                  {({ input, meta }) => (
                    <div className={fieldClass}>
                      <FormFieldLabel label={t('sell_contract.delivery_date')} />
                      <FormDateInput
                        value={typeof input.value === 'string' ? input.value : ''}
                        onChange={input.onChange}
                        onBlur={input.onBlur}
                        placeholder={t('sell_contract.date_placeholder')}
                        error={isFieldInvalid(meta)}
                      />
                      <FormFieldError show={isFieldInvalid(meta)} message={t('sell_contract.field_required')} />
                    </div>
                  )}
                </Field>
                <Field name="client_id">
                  {({ meta }) => {
                    const clientName = values.client_name ?? ''
                    const streetLine = [values.client_street, values.client_house_number]
                      .filter((part) => typeof part === 'string' && part.length > 0)
                      .join(' ')
                    const cityLine = [values.client_zip_code, values.client_place]
                      .filter((part) => typeof part === 'string' && part.length > 0)
                      .join(' ')
                    const taxNumber = values.client_tax_number ?? ''
                    return (
                      <div className={fieldClass}>
                        <FormFieldLabel label={t('sell_contract.client')} />
                        <ClientSearchSelect
                          selectedId={selectedId(values.client_id)}
                          selectedLabel={clientName}
                          placeholder={t('sell_contract.select')}
                          error={isFieldInvalid(meta)}
                          onSelect={(client) => {
                            applyClientFields(form.change, client)
                          }}
                          onClear={() => {
                            clearClientFields(form.change)
                          }}
                        />
                        <FormFieldError show={isFieldInvalid(meta)} message={t('sell_contract.field_required')} />
                        {clientName.length > 0 && (
                          <div className="self-stretch flex flex-col items-start gap-1 pt-1">
                            {taxNumber.length > 0 && (
                              <div className="text-foregrounds-fg-muted text-l font-medium font-Montserrat leading-4">
                                {t('invoice_form.nip', { number: taxNumber })}
                              </div>
                            )}
                            <div className="text-foregrounds-fg-base text-sm font-semibold font-Montserrat leading-5">
                              {clientName}
                            </div>
                            {streetLine.length > 0 && (
                              <div className="text-foregrounds-fg-muted text-l font-medium font-Montserrat leading-4">
                                {`ul. ${streetLine}`}
                              </div>
                            )}
                            {cityLine.length > 0 && (
                              <div className="text-foregrounds-fg-muted text-l font-medium font-Montserrat leading-4">
                                {cityLine}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )
                  }}
                </Field>
                <Field name="motorbike_id">
                  {({ meta }) => {
                    const vehicleName = values.motorbike_name ?? ''
                    return (
                      <div className={fieldClass}>
                        <FormFieldLabel label={t('sell_contract.vehicle')} />
                        <VehicleSearchSelect
                          selectedId={selectedId(values.motorbike_id)}
                          selectedLabel={vehicleName}
                          placeholder={t('sell_contract.select')}
                          error={isFieldInvalid(meta)}
                          onSelect={(motorbike: MarketPlaceMotorbike) => {
                            const fields = vehicleFieldsFromMotorbike(motorbike)
                            form.change('motorbike_id', fields.motorbike_id)
                            form.change('motorbike_name', fields.motorbike_name)
                            form.change('vin', fields.vin)
                            form.change('motorbike_mileage', fields.motorbike_mileage)
                            form.change('motorbike_year', fields.motorbike_year)
                          }}
                          onClear={() => {
                            form.change('motorbike_id', '')
                            form.change('motorbike_name', '')
                            form.change('vin', '')
                            form.change('motorbike_mileage', null)
                            form.change('motorbike_year', '')
                          }}
                        />
                        <FormFieldError show={isFieldInvalid(meta)} message={t('sell_contract.field_required')} />
                        {vehicleName.length > 0 && (
                          <div className="self-stretch flex flex-col items-start gap-1 pt-1">
                            <div className="text-foregrounds-fg-base text-sm font-semibold font-Montserrat leading-5">
                              {vehicleName}
                            </div>
                            <div className="text-foregrounds-fg-muted text-l font-medium font-Montserrat leading-4">
                              {t('sell_contract.vin_value', { vin: displayOrDash(values.vin) })}
                            </div>
                            <div className="text-foregrounds-fg-muted text-l font-medium font-Montserrat leading-4">
                              {t('sell_contract.mileage_value', {
                                mileage: mileageDisplayValue(values.motorbike_mileage, t('my_vehicles.km'))
                              })}
                            </div>
                            <div className="text-foregrounds-fg-muted text-l font-medium font-Montserrat leading-4">
                              {t('sell_contract.year_value', { year: displayOrDash(values.motorbike_year) })}
                            </div>
                          </div>
                        )}
                      </div>
                    )
                  }}
                </Field>
              </div>
              <div className="self-stretch h-px bg-borders-border-base" />
              <div className="self-stretch grid grid-cols-2 gap-8">
                <div className="flex justify-start items-start gap-4">
                  <Field name="currency_code">
                    {({ input, meta }) => (
                      <div className={`flex-1 ${fieldClass}`}>
                        <FormFieldLabel label={t('sell_contract.currency')} />
                        <FormSelect
                          value={typeof input.value === 'string' ? input.value : ''}
                          onChange={input.onChange}
                          placeholder={t('sell_contract.select')}
                          error={isFieldInvalid(meta)}
                          options={[
                            { value: 'PLN', label: t('sell_contract.pln') },
                            { value: 'EUR', label: t('sell_contract.eur') }
                          ]}
                        />
                        <FormFieldError show={isFieldInvalid(meta)} message={t('sell_contract.field_required')} />
                      </div>
                    )}
                  </Field>
                  <Field name="vat_option">
                    {({ input, meta }) => (
                      <div className={`flex-1 ${fieldClass}`}>
                        <FormFieldLabel label={t('sell_contract.vat_rate')} />
                        <FormSelect
                          value={typeof input.value === 'string' ? input.value : ''}
                          onChange={input.onChange}
                          placeholder={t('sell_contract.select')}
                          error={isFieldInvalid(meta)}
                          options={vatOptions.map((option) => ({ value: option.value, label: t(option.labelKey) }))}
                        />
                        <FormFieldError show={isFieldInvalid(meta)} message={t('sell_contract.field_required')} />
                      </div>
                    )}
                  </Field>
                </div>
                <Field name="price">
                  {({ input, meta }) => (
                    <div className={`flex-1 ${fieldClass}`}>
                      <FormFieldLabel label={t('sell_contract.price')} />
                      <div
                        className="self-stretch h-11 pl-4 pr-1 py-1 bg-backgrounds-bg-field-component rounded-md inline-flex justify-center items-center gap-1.5 overflow-hidden"
                        style={{ boxShadow: fieldBoxShadow(isFieldInvalid(meta)) }}>
                        <input
                          type="text"
                          inputMode="decimal"
                          value={typeof input.value === 'string' || typeof input.value === 'number' ? input.value : ''}
                          onChange={(event) => {
                            const nextValue = event.target.value
                            if (numericPricePattern.test(nextValue)) {
                              input.onChange(nextValue.replace(',', '.'))
                            }
                          }}
                          className="flex-1 h-full bg-transparent outline-none text-foregrounds-fg-base text-sm font-semibold font-Montserrat leading-5"
                        />
                        <InsetSegmentedControl
                          className="shrink-0"
                          value={priceType}
                          onChange={(nextType) => {
                            form.change('price_type', nextType)
                          }}
                          options={[
                            { value: 'gross', label: t('sell_contract.gross') },
                            { value: 'net', label: t('sell_contract.net') }
                          ]}
                        />
                      </div>
                      <FormFieldError show={isFieldInvalid(meta)} message={t('sell_contract.field_required')} />
                    </div>
                  )}
                </Field>
              </div>
              <PriceBreakdownCard
                netPrice={netPrice}
                vatAmount={vatAmount}
                vatRate={vatRate}
                grossPrice={grossPrice}
                currencySymbol={symbol}
              />
              <div className="self-stretch inline-flex justify-start items-start gap-8">
                <Field name="payment_method">
                  {({ input, meta }) => (
                    <div className={`flex-1 ${fieldClass}`}>
                      <FormFieldLabel label={t('sell_contract.payment_method')} />
                      <FormSelect
                        value={typeof input.value === 'string' ? input.value : ''}
                        onChange={input.onChange}
                        placeholder={t('sell_contract.select')}
                        error={isFieldInvalid(meta)}
                        options={[
                          { value: 'bank_transfer', label: t('sell_contract.bank_transfer') },
                          { value: 'cash', label: t('sell_contract.cash') }
                        ]}
                      />
                      <FormFieldError show={isFieldInvalid(meta)} message={t('sell_contract.field_required')} />
                    </div>
                  )}
                </Field>
                <Field name="payment_due_date">
                  {({ input, meta }) => (
                    <div className={`flex-1 ${fieldClass}`}>
                      <FormFieldLabel label={t('invoice_form.payment_due')} />
                      <FormDateInput
                        value={typeof input.value === 'string' ? input.value : ''}
                        onChange={input.onChange}
                        onBlur={input.onBlur}
                        placeholder={t('sell_contract.date_placeholder')}
                        error={isFieldInvalid(meta)}
                      />
                      <FormFieldError show={isFieldInvalid(meta)} message={t('sell_contract.field_required')} />
                    </div>
                  )}
                </Field>
              </div>
              <Field name="additional_text">
                {({ input }) => (
                  <div className={fieldClass}>
                    <FormFieldLabel label={t('invoice_form.additional_info')} />
                    <FormTextArea
                      value={typeof input.value === 'string' ? input.value : ''}
                      onChange={input.onChange}
                      placeholder=""
                    />
                  </div>
                )}
              </Field>
            </div>
            <FormFooter
              cancelLabel={t('my_vehicles.discard')}
              onCancel={onCancel}
              submitLabel={mode === 'edit' ? t('my_vehicles.save') : t('my_vehicles.add')}
              submitDisabled={submitting || loading}
              {...(mode === 'create' ? { submitIcon: <CheckSvg className="w-3.5 h-3.5 text-icon-primary" /> } : {})}
              contentClassName="max-w-[1000px] min-w-[720px]"
              isDirty={dirty}
            />
          </form>
        )
      }}
    />
  )
}

export default InvoiceForm
